import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RefreshCw, ClipboardCheck, AlertTriangle, CheckCircle2 } from "lucide-react";

const Contador = ({ valor, etiqueta, detalle, testid }) => (
  <div className="rounded-lg border p-3" data-testid={testid}>
    <div className="text-2xl font-semibold">{valor}</div>
    <div className="text-xs text-muted-foreground">{etiqueta}</div>
    {detalle ? <div className="text-xs text-muted-foreground mt-0.5">{detalle}</div> : null}
  </div>
);

export function EstadoIntegracionQBO({ companyId }) {
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setCargando(true);
    try {
      const { data } = await api.get(`/quickbooks/estado-integracion/${companyId}`);
      setEstado(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCargando(false);
    }
  }, [companyId]);

  useEffect(() => { cargar(); }, [cargar]);

  if (!estado) return null;

  // P31 (2026-09-21): "listo" = todo configurado; crear en QuickBooks además exige el interruptor
  // QBO_ESCRITURA_HABILITADA. Con la cuenta real y el interruptor apagado se dice explícitamente.
  const escrituraApagada = estado.escritura && estado.escritura.habilitada === false && !estado.conexion?.simulacion;
  const badge = estado.listo && escrituraApagada
    ? <Badge className="bg-sky-100 text-sky-900 hover:bg-sky-100" data-testid="qbo-estado-badge">Listo · escritura apagada</Badge>
    : estado.listo
    ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid="qbo-estado-badge">Listo para facturar</Badge>
    : estado.conexion?.simulacion
      ? <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100" data-testid="qbo-estado-badge">Modo simulación</Badge>
      : <Badge variant="secondary" data-testid="qbo-estado-badge">Falta configurar</Badge>;

  return (
    <Card className="mb-5" data-testid="qbo-estado-integracion">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2 font-heading">
          <ClipboardCheck className="h-5 w-5 text-primary" /> Estado de la integración {badge}
        </CardTitle>
        <Button size="sm" variant="outline" onClick={cargar} disabled={cargando} data-testid="qbo-estado-refresh">
          <RefreshCw className={`h-4 w-4 mr-1 ${cargando ? "animate-spin" : ""}`} /> Actualizar
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {escrituraApagada && (
          <p className="rounded-md border border-sky-200 bg-sky-50 text-sky-900 text-sm p-3" data-testid="qbo-estado-escritura-apagada">
            La escritura en QuickBooks está <strong>apagada</strong> ({estado.escritura.variable}=false): se puede
            conectar, mapear y ver la vista previa exacta de cada factura, pero <strong>no se crea nada</strong> en
            QuickBooks hasta que se active a propósito.
          </p>
        )}
        {estado.conexion?.simulacion && (
          <p className="text-sm text-muted-foreground" data-testid="qbo-estado-simulacion">
            Las facturas se simulan contra un catálogo de prueba: no se crea nada en QuickBooks hasta conectar la cuenta real de Álvarez Trucking Inc.
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Contador testid="qbo-estado-dat" valor={estado.dat?.vinculados_a_dat ?? 0} etiqueta="Viajes vinculados a DAT"
                    detalle={`de ${estado.dat?.viajes_activos ?? 0} · ${estado.dat?.porcentaje_vinculado ?? 0}%`} />
          <Contador testid="qbo-estado-clientes" valor={estado.mapeos?.clientes_mapeados ?? 0} etiqueta="Clientes mapeados"
                    detalle={`de ${estado.mapeos?.clientes_total ?? 0}`} />
          <Contador testid="qbo-estado-rutas" valor={estado.mapeos?.rutas_mapeadas ?? 0} etiqueta="Rutas mapeadas"
                    detalle={`de ${estado.mapeos?.rutas_total ?? 0}`} />
          <Contador testid="qbo-estado-facturados" valor={estado.facturacion?.facturados ?? 0} etiqueta="Facturados"
                    detalle={`${estado.facturacion?.por_facturar ?? 0} por facturar`} />
        </div>

        {estado.faltantes?.length ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm p-3" data-testid="qbo-estado-faltantes">
            <p className="font-semibold flex items-center gap-2 mb-1"><AlertTriangle className="h-4 w-4" /> Falta para poder facturar de verdad</p>
            <ul className="list-disc pl-5 space-y-0.5">
              {estado.faltantes.map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-emerald-700 flex items-center gap-2" data-testid="qbo-estado-listo">
            <CheckCircle2 className="h-4 w-4" /> {escrituraApagada
              ? "Todo configurado — falta solo activar la escritura en QuickBooks."
              : "Todo listo — las facturas se crean directo en QuickBooks."}
          </p>
        )}

        {estado.conexion?.catalogo_error && (
          <p className="text-sm text-red-700" data-testid="qbo-estado-catalogo-error">
            No se pudo leer el catálogo de QuickBooks: {estado.conexion.catalogo_error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
